import { HttpClient, HttpResponse } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { UserService } from './user.service';
import { parse } from 'fsp-xml-parser'
import { MapService } from './map.service';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import { TabService } from './tab.service';
import { MatTabBody } from '@angular/material/tabs';
import { Extent } from 'ol/extent';
import { environment, ui_search_fields } from './../environments/environment';
import { MessageService } from './message.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Collection } from './data/collection';
import { Record } from './data/record';

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  EPSG_3857 = "EPSG:3857"; // Spherical Mercator projection used by most web map applications (e.g Google, OpenStreetMap, Mapbox).
  EPSG_4326 = "EPSG:4326"; // Standard coordinate system used in cartography, geodesy, and navigation (including GPS).

  selectedCollection: Collection|undefined = undefined;
  searchResultCount: number = 0;
  searchResultMoreAvailable: boolean = false;
  searchResult = signal<Record[]>([]);

  maxRecords: number = 100; // Our limit, the servers might have a lower limit

  collectionFilterText:string = '';

  collections = signal<Collection[]>([]);

  enmapCollection = new Collection(
    'ENMAP_HSI_L2A', 
    'ENMAP Level 2 A',
    'EnMAP L2A HSI Products providing all 224 bands as CEOS-ARD data using athmosperic correction over land (CARD4L). This Collection holds EnMAP L2A HSI Products with \'nominal quality\', \'reduced quality\' and \'low quality\'. Those can be distinguished by using the property \'enmap:overallQuality\', here the values respond to {0}: nominal quality, {1}: reduced quality and {2}: low quality.',
    'https://geoservice.dlr.de/eoc/ogc/stac/v1',
    'https://geoservice.dlr.de/eoc/ogc/stac/v1/collections/ENMAP_HSI_L2A',
    'https://geoservice.dlr.de/eoc/ogc/stac/v1/https://geoservice.dlr.de/eoc/ogc/stac/v1/collections/ENMAP_HSI_L2A/queryables'
  );

  constructor(public userService: UserService, 
      private http: HttpClient, 
      public mapService:MapService, 
      public tabService:TabService, 
      public messageService:MessageService) {
  }


  filteredCollectionList() {
    return this.collections().filter(item =>
      item.title.toLowerCase().includes(this.collectionFilterText.toLowerCase())
    );
  }

  listCollections(): void {
    if (environment.publicStacCatalogue) {
      this.http.get<any[]>(
        environment.publicStacCatalogue + '/collections?limit=500', 
        {headers: new HttpHeaders({
          'Accept': 'application/json',
        })}).subscribe({
          next: (data: any) => {
            this.collections.set(this.processPublicCollectionResponse(data));
          },
          error: (e) => {
            this.userService.handleError(e, 'Failed to get public collections');
          }
        });
    } 
  }

  private processPublicCollectionResponse(data:any): Collection[] {
    let collectionArray = data['collections'];
    let tmpCollections: Collection[] = [];
    for (let col of collectionArray) {
      //console.log(col);
      if (col.id == 'cop-dem-eea-10-laea-tif'
        || col.id == 'ccm-optical'
        || col.id == 'ccm-sar' ) {
        // Some collections have restricted access:
        // CopDEM TIFF (10 m)
        // Copernicus Contributing Missions Optical
        // Copernicus Contributing Missions SAR
        continue;
      }
      let rootUrl = undefined;
      let collectionUrl = undefined;
      let queryablesUrl = undefined;
      let linkArray = col.links;
      for (let link of linkArray) {
        if (link.rel == 'self') {
          collectionUrl = link.href;
        } else if (link.rel == 'queryables') {
          queryablesUrl = link.href;
        } else if (link.rel == 'root') {
          rootUrl = link.href; // Root URL is used for search
        }
      }
      let s3Asset = false;
      if ('item_assets' in col) {
        for (const key in col.item_assets) {
          let asset = col.item_assets[key];
          if ('auth:refs' in asset && asset['auth:refs'].includes('s3')) {
            // asset.type.startsWith('image') 
            s3Asset = true;
            break;
          }
        }
        if (s3Asset) {
          //console.log('Adding ' + col.title);
          let collection = new Collection(col.id, col.title, col.description, rootUrl, collectionUrl, queryablesUrl);
          if (ui_search_fields && ui_search_fields[collection.id]) {
            collection.uiSearchFields = ui_search_fields[collection.id];
          }
          tmpCollections.push(collection);
        }
      }
    }
    tmpCollections.push(this.enmapCollection);
    
    // Sort collections by name
    tmpCollections.sort((a,b) => (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0))

    // User's own data collection always last 
    if (this.userService.userFileCollection) {
      tmpCollections.push(this.userService.userFileCollection);
    }

    return tmpCollections;
  }

  selectCollection(collection: Collection|undefined) {
    this.selectedCollection = collection;
  }

  searchCollection(searchConstraints:any): void {
    //startDate:string|undefined, 
    //endDate:string|undefined, 
    //cloudCoverage:number|undefined,
    //title:string|undefined
    this.mapService.clearResultLayerFeatures();
    if (this.selectedCollection != undefined) {
      if (this.selectedCollection.id == 'ENMAP_HSI_L2A') {
        this.searchStacCollectionENMAP(searchConstraints);
      } else {
        this.searchStacCollection(this.selectedCollection, searchConstraints);
      }
    }
  }

  searchStacCollection(collection:Collection, searchConstraints:any) {
    console.log('Searching collection ' + collection.id);
    //console.log(collection);
    let query: { [key: string]: any } = {};

    query['collections'] = [ collection.id ] ;

    // dates should be strings in format yyyy-mm-dd
    if (searchConstraints['startDate'] && searchConstraints['endDate']) {
      query['datetime'] = searchConstraints['startDate'] + 'T00:00:00Z/' + searchConstraints['endDate'] + 'T23:59:59Z';
    } else if (searchConstraints['startDate']) {
      query['datetime'] = searchConstraints['startDate'] + 'T00:00:00Z/..';
    } else if (searchConstraints['endDate']) {
      query['datetime'] = '../' + searchConstraints['endDate'] + 'T23:59:59Z';
    }

    // bbox = [minx, miny, maxx, maxy]
    let bbox = this.mapService.getSearchBoundingBox();
    if (bbox) {
      query['bbox'] = bbox;
    }

    let filters: any[] = [];

    if (collection.id == 'metadata:main') {
      // Special filter for user's data - otherwise processing output collections 
      // are also listed in addition to the items
      let filter = { 'op':'=', 'args':[ {'property': 'type'}, 'item' ] };
      filters.push(filter);
    }
    for (let key in searchConstraints) {
      if (key === 'startDate' || key === 'endDate') {
        continue;
      } else if (key === 'cloudCover' && searchConstraints[key]) {
        let filter = { 'op': 'between', 'args': [ {'property':key}, 0, +searchConstraints[key] ] };
        filters.push(filter);
      } else if (key === 'id' && searchConstraints[key]) {
        let filter = { 'op': 'like', 'args': [ {'property':key}, '%'+searchConstraints[key]+'%' ] };
        filters.push(filter);
      } else if (searchConstraints[key]) {
        let filter = { 'op': '=', 'args': [ {'property':key}, searchConstraints[key] ] };
        filters.push(filter);
      }
    }
    if (filters.length > 0) {
      query['filter-lang'] = 'cql2-json';
      if (filters.length == 1) {
        query['filter'] = filters[0];
      } else {
        query['filter'] = { 'op':'and', 'args':filters };
      }
    }

    // Limit the number of items to return
    query['limit'] = this.maxRecords;
    // Show newest data first
    if (collection.sortSupported) {
      query['sortby'] = [ {'field':'properties.datetime', 'direction':'desc'}, {'field':'id', 'direction':'asc'}];
    }

    console.log(query);

    let headers;
    if (collection.id == 'metadata:main') {
      headers = new HttpHeaders({
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + this.userService.getAccessToken()
      });
    } else {
      headers = new HttpHeaders({
        'Accept': 'application/json',
      });
    }

    this.http.post(collection.rootUrl + '/search', query, 
    {headers: headers, responseType:'text', observe: 'response'}).subscribe(resp => {
      if (resp.status == 200) {
        let records = undefined;
        [this.searchResultCount, records, this.searchResultMoreAvailable] = this.parseStacResults(resp);
        for (let rec of records) {
          this.mapService.addResultLayerFeature(rec.feature!);
          //this.mapService.addWMSLayer(this.wmsUrl, rec.feature!.get('title'));
        }
        this.searchResult.set(records);
        this.tabService.showBottomViewResults();
      } else {
        this.messageService.addMessage('Search failed (HTTP response ' + resp.status.toString() + ' ' + resp.statusText + ')');
        this.tabService.showBottomViewMessages();
      }
    });
}

  searchStacCollectionENMAP(searchConstraints:any) {
    let stacUrl = 'https://geoservice.dlr.de/eoc/ogc/stac/v1/search?f=json';
    console.log('Searching ' + stacUrl);

    let query: { [key: string]: any } = {};

    query['collections'] = [ 'ENMAP_HSI_L2A' ] ;

    // dates should be strings in format yyyy-mm-dd
    if (searchConstraints['startDate'] && searchConstraints['endDate']) {
      query['datetime'] = searchConstraints['startDate'] + 'T00:00:00Z/' + searchConstraints['endDate'] + 'T23:59:59Z';
    } else if (searchConstraints['startDate']) {
      var today = new Date();
      var dd = String(today.getDate()).padStart(2, '0');
      var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
      var yyyy = today.getFullYear();
      query['datetime'] = searchConstraints['startDate'] + 'T00:00:00Z/' + yyyy + '-' + mm + '-' + dd + 'T23:59:59Z';
    } else if (searchConstraints['endDate']) {
      query['datetime'] = '2022-04-27T00:00:00Z/' + searchConstraints['endDate'] + 'T23:59:59Z';
    }

    // bbox = [minx, miny, maxx, maxy]
    let bbox = this.mapService.getSearchBoundingBox();
    if (bbox) {
      query['bbox'] = bbox;
    }

    let filters: any[] = [];
    for (let key in searchConstraints) {
      if (key === 'startDate' || key === 'endDate') {
        continue;
      } else if (key === 'eo:cloud_cover' && searchConstraints[key]) {
        let filter = { 'op': 'z', 'args': [ {'property':key}, +searchConstraints[key] ] };
        filters.push(filter);
//      } else if (key === 'id' && searchConstraints[key]) {
//        let filter = { 'op': 'like', 'args': [ {'property':key}, '%'+searchConstraints[key]+'%' ] };
//        filters.push(filter);
//      } else if (searchConstraints[key]) {
//        let filter = { 'op': '=', 'args': [ {'property':key}, searchConstraints[key] ] };
//        filters.push(filter);
      }
    }
    if (filters.length > 0) {
      query['filter-lang'] = 'cql2-json';
      if (filters.length == 1) {
        query['filter'] = filters[0];
      } else {
        query['filter'] = { 'op':'and', 'args':filters };
      }
    }

    // Limit the number of items to return
    query['limit'] = this.maxRecords;
    // Show newest data first
    query['sortby'] = [ {'field':'properties.datetime', 'direction':'desc'}, {'field':'id', 'direction':'asc'}];
    
    //console.log(query);

    this.http.post(stacUrl, query, 
    {headers: new HttpHeaders({
      'Accept': 'application/json',
    }), responseType:'text', observe: 'response'}).subscribe(resp => {
      if (resp.status == 200) {
        let records = undefined;
        [this.searchResultCount, records, this.searchResultMoreAvailable] = this.parseStacResults(resp);
        for (let rec of records) {
          this.mapService.addResultLayerFeature(rec.feature!);
          //this.mapService.addWMSLayer(this.wmsUrl, rec.feature!.get('title'));
        }
        this.searchResult.set(records);
        this.tabService.showBottomViewResults();
      } else {
        this.messageService.addMessage('Search failed (HTTP response ' + resp.status.toString() + ' ' + resp.statusText + ')');
      }
    });
  }

  parseStacResults(result:HttpResponse<string>): [number,Record[],boolean] {
    let results: Record[] = [];
    let resultCount = 0;
    if (result.body == null) {
      return [resultCount,results,false];
    }
    let json = JSON.parse(result.body);
    for (let f of json.features) {
      //console.log(f);
      if (!('properties' in f)) {
        console.log('Ignoring ' + JSON.stringify(f));
        continue;
      } 
      let rec = new Record();
      rec.id = f.id;
      rec.collection = f.collection;
      rec.title = f.id;
      rec.published = f.properties.datetime;
      for (let l of f.links) {
        if (l.rel == 'self') {
          // Prefer S3 links
          if (!rec.itemHref || !rec.itemHref.startsWith('s3://')) {
            rec.itemHref = l.href;
            rec.resourceUrl = l.href;
          }
        }
      }
      
      let feature = new Feature();
      feature.set('id', rec.id);
      feature.set('title', rec.id);
      feature.set('published', rec.published);
      if (f.bbox) {
        let minx: number = f.bbox[0];
        let miny: number = f.bbox[1];
        let maxx: number = f.bbox[2];
        let maxy: number = f.bbox[3];
        let poly = new Polygon([[
          [minx, miny],
          [maxx, miny],
          [maxx, maxy],
          [minx, maxy],
          [minx, miny]]]).transform(this.EPSG_4326, this.EPSG_3857);
        feature.setGeometry(poly);
      }
      rec.feature = feature;
      results.push(rec);
    }
    let moreAvailable = false;
    if (json.links) {
      for (let l of json.links) {
        if (l.rel == 'next') {
          moreAvailable = true;
          break;
        }
      }
    }
    //console.log(results);
    return [ results.length, results, moreAvailable ];
  }

  getRecordDetails(rec:Record): Observable<any>|null {
    if (rec.resourceUrl) {
      if (environment.publicStacCatalogue && rec.resourceUrl.startsWith(environment.publicStacCatalogue)) {
        return this.http.get(rec.resourceUrl, 
          {headers: new HttpHeaders({
            'Accept': 'application/json',
          })});
        } else if (rec.resourceUrl.startsWith(this.enmapCollection.collectionUrl)) {
          return this.http.get(rec.resourceUrl, 
            {headers: new HttpHeaders({
              'Accept': 'application/geo+json',
            })});
        } else {
          return this.http.get(rec.resourceUrl, 
            {headers: new HttpHeaders({
              'Authorization': 'Bearer ' + this.userService.getAccessToken(),
              'Accept': 'application/json',
            })});
      }
    }
    return null;
  }
}

export class Filter {
  operator: string;
  valueReference: string;
  literal: string;
  attributes: string = '';

  constructor(operator:string, attributes:string, valueReference:string, literal:string) {
    this.operator = operator;
    this.valueReference = valueReference;
    this.literal = literal;
    this.attributes = attributes;
  }
}